import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleSecondVideoWarmup();
  }

  #controller = new AbortController();
  #warmupHandle;
  #warmupUsesIdleCallback = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    if (this.#warmupHandle != null) {
      if (this.#warmupUsesIdleCallback) window.cancelIdleCallback?.(this.#warmupHandle);
      else window.clearTimeout(this.#warmupHandle);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const featuredMediaId = event.detail.resource?.featured_media?.id;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    if (newMediaGallery.dataset.respectProductMediaOrder !== 'true' || !featuredMediaId) return;

    requestAnimationFrame(() => {
      const media = newMediaGallery.querySelector(`[data-media-id="${featuredMediaId}"]`);
      const slide = media?.closest('slideshow-slide');
      const slides = Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
      const index = slide ? slides.indexOf(slide) : -1;

      if (index >= 0) newMediaGallery.slideshow?.select(index, undefined, { animate: false });
    });
  };

  /** @returns {boolean} */
  #shouldAvoidAutomaticVideo() {
    const connection = /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */ (navigator.connection);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return Boolean(
      connection?.saveData ||
        reduceMotion ||
        connection?.effectiveType === 'slow-2g' ||
        connection?.effectiveType === '2g'
    );
  }

  /**
   * Warms only the second Product Page V2 slide after the cover has loaded.
   * The video remains paused until the customer reaches it.
   */
  #scheduleSecondVideoWarmup() {
    if (this.dataset.warmSecondVideo !== 'true' || this.#shouldAvoidAutomaticVideo()) return;

    const warmVideo = () => {
      const secondSlide = this.querySelectorAll('slideshow-slide')[1];
      const deferredMedia = secondSlide?.querySelector('deferred-media');
      if (!deferredMedia || deferredMedia.dataset.rdWarmed === 'true') return;

      deferredMedia.dataset.rdWarmed = 'true';
      const templateVideo = deferredMedia.querySelector('template')?.content.querySelector('video');
      templateVideo?.removeAttribute('autoplay');
      deferredMedia.loadContent?.(false);

      const video = deferredMedia.querySelector('video');
      if (video) {
        video.preload = 'auto';
        video.muted = true;
        video.playsInline = true;
        video.load();
      }
    };

    if ('requestIdleCallback' in window) {
      this.#warmupUsesIdleCallback = true;
      this.#warmupHandle = window.requestIdleCallback(warmVideo, { timeout: 2000 });
    } else {
      this.#warmupHandle = window.setTimeout(warmVideo, 900);
    }
  }

  /**
   * Plays and loops the selected native Shopify product video.
   * Moving away pauses it; returning to the slide resumes it without a second download.
   * Reduced Motion, Data Saver and slow-network visitors keep manual playback.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideSelect = (event) => {
    if (this.dataset.autoplayVideoOnSelect !== 'true') return;

    const selectedMedia = event.detail.slide?.querySelector('deferred-media');

    this.querySelectorAll('deferred-media').forEach((media) => {
      if (media !== selectedMedia) media.pauseMedia?.();
    });

    if (!selectedMedia || this.#shouldAvoidAutomaticVideo()) return;

    selectedMedia.loadContent?.(false);

    requestAnimationFrame(() => {
      const video = selectedMedia.querySelector('video');
      if (video) {
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
      }
      selectedMedia.playMedia?.();
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
